import { fail, success } from '@helpers/result-helper.js'
import toast from '@services/toasts/index.js'
import { isSuccess } from '@/helpers/http-status-helpers'
import { VuexResponse } from '@/helpers/vuex-action-builder'
import { CandidatePreferenceUpdateMode } from '@/shared/constants/candidate/CandidatePreferenceUpdateMode'
import WorkplaceRegisterExportFailureToasts from '@/shared/constants/workplaceRegister/WorkplaceRegisterExportFailureToasts'

const defaultCandidateListFilters = () => ({
  sortKey: null,
  sortDir: 'asc',
  bookable: null,
  certificationIds: [],
  clientGroupIds: [],
  typeIds: [],
})

export default {
  namespaced: true,
  state: {
    loadingCount: 0,
    crudLoadingCount: 0,
    file: null,
    candidateList: {
      filters: defaultCandidateListFilters(),
      searchTerm: '',
    },
  },
  getters: {
    moduleName: () => 'candidate',
    isLoading: (state) => state.loadingCount > 0,
    isLoadingCRUD: (state) => state.crudLoadingCount > 0,
    candidateListFilters: (state) => state.candidateList.filters,
    candidateListSearchTerm: (state) => state.candidateList.searchTerm.trim(),
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    SET_CANDIDATE_LIST_FILTERS(state, partial) {
      state.candidateList.filters = {
        ...state.candidateList.filters,
        ...partial,
      }
    },
    RESET_CANDIDATE_LIST_FILTERS(state) {
      state.candidateList.filters = defaultCandidateListFilters()
    },
    SET_CANDIDATE_LIST_SEARCH_TERM(state, value) {
      state.candidateList.searchTerm =
        typeof value === 'string' ? value : String(value ?? '')
    },
  },
  actions: {
    async loadCandidate({ commit }, payload) {
      const id = typeof payload === 'object' ? payload.id : payload
      const silent =
        typeof payload === 'object' ? payload.silent ?? false : false

      commit('START_LOADING')
      try {
        const response = await this.$api.candidate.getCandidateDetails(id)

        if (isSuccess(response.status)) {
          return success(response.data)
        }
      } catch {
        if (!silent) toast.error('Candidate data failed to load')
        return fail()
      } finally {
        commit('FINISH_LOADING')
      }
    },

    async getCandidates({ commit }, payload) {
      return await new VuexResponse(commit)
        .request(() => this.$api.candidate.getCandidates(payload))
        .go()
    },

    async queryCandidates({ commit }, payload) {
      return await new VuexResponse(commit)
        .request(() => this.$api.candidate.queryCandidates(payload))
        .go()
    },

    async setCandidatePreferred({ commit }, { candidateId, payload }) {
      const changeModes = payload.changes.map((change) => change.mode)
      const allChangesRemoved = changeModes.every(
        (mode) => mode === CandidatePreferenceUpdateMode.None
      )
      const allChangesAdded = changeModes.every(
        (mode) => mode === CandidatePreferenceUpdateMode.Locations
      )
      let successToastKey = 'candidateList.preference.updated.success'
      if (allChangesRemoved) {
        successToastKey = 'candidateList.preference.removed.success'
      } else if (allChangesAdded) {
        successToastKey = 'candidateList.preference.added.success'
      }

      return await new VuexResponse(commit)
        .request(() =>
          this.$api.candidate.setCandidatePreferred(candidateId, payload)
        )
        .withSuccessToast(this.$i18n.t(successToastKey))
        .go()
    },

    async getCandidatePreferredDetails({ commit }, { candidateId, payload }) {
      return await new VuexResponse(commit)
        .request(() =>
          this.$api.candidate.getCandidatePreferredDetails(candidateId, payload)
        )
        .go()
    },

    setCandidateListFilters({ commit }, partial) {
      commit('SET_CANDIDATE_LIST_FILTERS', partial)
    },

    setCandidateListSearchTerm({ commit }, value) {
      commit('SET_CANDIDATE_LIST_SEARCH_TERM', value)
    },

    resetCandidateListFilters({ commit }) {
      commit('RESET_CANDIDATE_LIST_FILTERS')
    },

    clearCandidateListSearchAndFilters({ commit }) {
      commit('SET_CANDIDATE_LIST_SEARCH_TERM', '')
      commit('RESET_CANDIDATE_LIST_FILTERS')
    },

    async getCandidateListCertifications({ commit }, clientIds = []) {
      if (clientIds.length === 0) return null

      return await new VuexResponse(commit)
        .request(() =>
          this.$api.candidate.getCandidateListCertifications(clientIds)
        )
        .withFailureToast(
          this.$i18n.t('candidateList.error.loadCertificationsFailed')
        )
        .go()
    },

    async getCandidateListTypes({ commit }, clientIds = []) {
      if (clientIds.length === 0) return null

      return await new VuexResponse(commit)
        .request(() => this.$api.candidate.getCandidateListTypes(clientIds))
        .withFailureToast(this.$i18n.t('candidateList.error.loadTypesFailed'))
        .go()
    },

    async getCandidateAvailability(
      { commit },
      { candidateId, startDate, endDate, timeZone }
    ) {
      return await new VuexResponse(commit)
        .request(() =>
          this.$api.candidate.getCandidateAvailability(
            candidateId,
            startDate,
            endDate,
            timeZone
          )
        )
        .go()
    },

    async exportCandidates(
      { commit },
      { locationIds, startDate, endDate, format, useACECQATemplate }
    ) {
      const { data, statusCode } = await new VuexResponse(commit)
        .request(() =>
          this.$api.candidate.exportCandidates(
            locationIds,
            startDate,
            endDate,
            format,
            useACECQATemplate
          )
        )
        .withSuccessToast(this.$i18n.t('candidateExport.exportSuccessText'))
        .go()

      if (data) return data

      toast.error(
        this.$i18n.t(
          WorkplaceRegisterExportFailureToasts[statusCode.toString()] ||
            'candidateExport.errors.generic'
        )
      )

      return null
    },
  },
}
